import type { RiskAssessment } from '@nestjs-boilerplate/types';
import { useAtomValue } from 'jotai';
import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';

import { Checkbox } from '@/components/ui/checkbox';
import { DataCardList } from '@/components/ui/data-card-list';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { generatingAssessmentIdsAtom } from '@/stores/generating.store';

interface InProgressTableProps {
  assessments: RiskAssessment[];
  onContinue: (assessment: RiskAssessment) => void;
  onDelete: (assessment: RiskAssessment) => void;
  headerActions?: ReactNode;
}

export function InProgressTable({
  assessments,
  onContinue,
  onDelete,
  headerActions,
}: InProgressTableProps) {
  const isMobile = useIsMobile();
  const generatingIds = useAtomValue(generatingAssessmentIdsAtom);

  if (assessments.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-[20px] font-semibold text-[#051923]">In Progress Risk Assessments</h2>
        {headerActions}
      </div>

      {isMobile ? (
        <DataCardList<RiskAssessment>
          data={assessments}
          getRowId={(row) => row.id}
          cardConfig={{
            header: (row) => (
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-[#051923]">{row.optimalKycRef}</span>
                <span className="text-sm text-muted-foreground">Ref: {row.clientRef}</span>
              </div>
            ),
            fields: [{ label: 'Client Name', render: (row) => row.clientName }],
            footer: (row) => {
              const isGenerating = generatingIds.has(row.id);
              if (isGenerating) {
                return (
                  <div className="flex items-center gap-2 text-[#051923]">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Generating Report...</span>
                  </div>
                );
              }
              return (
                <div className="flex items-center justify-between gap-4" data-no-row-click>
                  <button
                    type="button"
                    onClick={() => onContinue(row)}
                    className="text-[#051923] underline hover:no-underline"
                    title="Continue the risk assessment from where you left it"
                  >
                    Continue Risk Assessment
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(row)}
                    className="text-[#051923] underline hover:no-underline"
                  >
                    Delete
                  </button>
                </div>
              );
            },
          }}
        />
      ) : (
        <div className="rounded-md border">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox disabled />
                </TableHead>
                <TableHead className="w-1/5">Optimal KYC Ref</TableHead>
                <TableHead className="w-1/5">Client Ref</TableHead>
                <TableHead className="w-1/5">Client Name</TableHead>
                <TableHead className="w-2/5">Options</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assessments.map((assessment) => {
                const isGenerating = generatingIds.has(assessment.id);

                return (
                  <TableRow key={assessment.id}>
                    <TableCell>
                      <Checkbox disabled />
                    </TableCell>
                    <TableCell className="font-medium text-[#051923]">
                      {assessment.optimalKycRef}
                    </TableCell>
                    <TableCell className="text-[#051923]">{assessment.clientRef}</TableCell>
                    <TableCell className="text-[#051923]">{assessment.clientName}</TableCell>
                    <TableCell>
                      {isGenerating ? (
                        <div className="flex items-center gap-2 text-[#051923]">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Generating Report...</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-4">
                          <button
                            type="button"
                            onClick={() => onContinue(assessment)}
                            className="text-[#051923] underline hover:no-underline"
                            title="Continue the risk assessment from where you left it"
                          >
                            Continue Risk Assessment
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(assessment)}
                            className="ml-auto text-[#051923] underline hover:no-underline"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
